import { useCallback, useEffect, useRef, useState } from 'react';

const YT_API_SRC = 'https://www.youtube.com/iframe_api';
const STATE_RESPONSE_TIMEOUT_MS = 5000;

const isValidVideoId = (id) => typeof id === 'string' && id.length === 11;
const isFiniteTime = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const clampVolume = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return undefined;
  }
  return Math.min(Math.max(Math.round(value), 0), 100);
};

// One YouTube player kept in step with the rest of the room over the channel
// socket.
//
// Roles. The page that IS the stream (the OBS player overlay) passes
// `reportPlayerState: true` and reports its player's own transitions with a
// position (autoplay after a load, buffering, the end of the video) so the
// server and every other page can follow it. Every other page is a
// controller or a follower: it broadcasts only deliberate actions from its
// own controls, and those go out as bare commands with no position unless a
// position was given explicitly. Receivers act in place when a command has
// no position. That keeps a controller whose own copy of the video is out of
// step (cued at the start, half a second behind) from dragging the stream
// along with it.
export const useSyncedYouTubePlayer = ({
  videoId,
  startSeconds,
  endSeconds,
  channelConnected,
  addChannelListener,
  removeChannelListener,
  emitToChannel,
  initialVolume,
  defaultMuted,
  onLocalPlay,
  onLocalPause,
  onLocalSeek,
  onLocalEnd,
  autoPlayOnReady = true,
  reportPlayerState = false
}) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [apiReady, setApiReady] = useState(() => !!(window.YT && window.YT.Player));
  const [playerReady, setPlayerReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const initialVolumeState = clampVolume(initialVolume);
  const [volume, setVolume] = useState(() => initialVolumeState ?? 100);
  const [muted, setMuted] = useState(() => Boolean(defaultMuted));
  // YouTube's own paused state, so a page can cover its paused screen.
  const [isPaused, setIsPaused] = useState(false);
  const callbacksRef = useRef({ onLocalPlay, onLocalPause, onLocalSeek, onLocalEnd });
  const reportStateRef = useRef(Boolean(reportPlayerState));
  const socketRef = useRef({ addChannelListener, removeChannelListener, emitToChannel, channelConnected });
  const videoIdRef = useRef(videoId);
  const pendingStateRequestRef = useRef(null); // cancel fn for an in-flight sync
  const lastKnownTimeRef = useRef(0);
  const suppressUntilRef = useRef(0);
  const previousReportedTimeRef = useRef(0);
  const pollTimeRef = useRef(null);
  const advanceStreakRef = useRef(0);
  const lastNonZeroVolumeRef = useRef(
    initialVolumeState && initialVolumeState > 0 ? initialVolumeState : 50
  );
  const startSecondsRef = useRef(startSeconds || 0);
  // Clipped submissions stop at `endSeconds` (0 = play to the end).
  const endSecondsRef = useRef(endSeconds || 0);
  const clipEndFiredRef = useRef(false);
  const isHiddenRef = useRef(typeof document !== 'undefined' ? document.hidden : false);

  useEffect(() => {
    startSecondsRef.current = startSeconds || 0;
    clipEndFiredRef.current = false;
  }, [startSeconds]);

  useEffect(() => {
    endSecondsRef.current = endSeconds || 0;
    clipEndFiredRef.current = false;
  }, [endSeconds]);

  useEffect(() => {
    callbacksRef.current = {
      onLocalPlay: typeof onLocalPlay === 'function' ? onLocalPlay : undefined,
      onLocalPause: typeof onLocalPause === 'function' ? onLocalPause : undefined,
      onLocalSeek: typeof onLocalSeek === 'function' ? onLocalSeek : undefined,
      onLocalEnd: typeof onLocalEnd === 'function' ? onLocalEnd : undefined
    };
  }, [onLocalPlay, onLocalPause, onLocalSeek, onLocalEnd]);

  useEffect(() => {
    reportStateRef.current = Boolean(reportPlayerState);
  }, [reportPlayerState]);

  useEffect(() => {
    socketRef.current = { addChannelListener, removeChannelListener, emitToChannel, channelConnected };
  }, [addChannelListener, removeChannelListener, emitToChannel, channelConnected]);

  useEffect(() => {
    videoIdRef.current = videoId;
    // A sync reply that was asked for the previous video must not land on
    // this one.
    if (pendingStateRequestRef.current) {
      pendingStateRequestRef.current();
      pendingStateRequestRef.current = null;
    }
  }, [videoId]);

  // Our own commands make YouTube emit state changes. Those echoes are not
  // news to anyone, so the state-change handler ignores them for a moment.
  const markSuppressed = useCallback(() => {
    suppressUntilRef.current = Date.now() + 750;
  }, []);

  const ensurePlayerSize = useCallback(() => {
    const iframe = playerRef.current?.getIframe?.();
    if (!iframe) return;
    if (iframe.style.width !== '100%') iframe.style.width = '100%';
    if (iframe.style.height !== '100%') iframe.style.height = '100%';
  }, []);

  const handlePlayerReady = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;

    ensurePlayerSize();

    const dur = player.getDuration?.();
    if (typeof dur === 'number' && dur > 0 && !Number.isNaN(dur)) {
      setDuration(dur);
    }

    // If a start offset was requested, seek to it and use that as the initial time
    const startOffset = startSecondsRef.current || 0;
    if (startOffset > 0 && typeof player.seekTo === 'function') {
      player.seekTo(startOffset, true);
      lastKnownTimeRef.current = startOffset;
      previousReportedTimeRef.current = startOffset;
      setCurrentTime(startOffset);
    } else {
      const time = player.getCurrentTime?.();
      if (typeof time === 'number' && !Number.isNaN(time)) {
        lastKnownTimeRef.current = time;
        previousReportedTimeRef.current = time;
        setCurrentTime(time);
      }
    }

    // Start playback when ready only if allowed
    if (autoPlayOnReady && typeof player.playVideo === 'function') {
      player.playVideo();
    }

    setPlayerReady(true);

    if (typeof initialVolumeState === 'number') {
      const clamped = initialVolumeState;
      player.setVolume(clamped);
      setVolume(clamped);
      if (clamped <= 0) {
        player.mute();
        setMuted(true);
      } else {
        player.unMute();
        setMuted(false);
        lastNonZeroVolumeRef.current = clamped;
      }
      return;
    }

    if (defaultMuted) {
      player.mute();
      setMuted(true);
      return;
    }

    player.unMute();
    setMuted(false);
    const currentVolume = clampVolume(player.getVolume?.());
    if (typeof currentVolume === 'number') {
      setVolume(currentVolume);
      if (currentVolume > 0) {
        lastNonZeroVolumeRef.current = currentVolume;
      }
    }
  }, [defaultMuted, initialVolumeState, ensurePlayerSize, autoPlayOnReady]);

  const handlePlayerStateChange = useCallback((event = {}) => {
    if (!window.YT) return;

    const player = event.target || playerRef.current;
    if (!player) return;

    const now = Date.now();
    const time = player.getCurrentTime?.();
    const dur = player.getDuration?.();

    if (typeof dur === 'number' && dur > 0 && !Number.isNaN(dur)) {
      setDuration(dur);
    }

    let current = previousReportedTimeRef.current;
    if (typeof time === 'number' && !Number.isNaN(time)) {
      current = time;
      lastKnownTimeRef.current = time;
      setCurrentTime((prev) => (typeof prev === 'number' && Math.abs(prev - time) < 0.05 ? prev : time));
    }

    const suppressed = suppressUntilRef.current && suppressUntilRef.current > now;
    const pageHidden = isHiddenRef.current === true;
    const playerState = event.data;
    // Mirror YouTube's paused state for the UI before any early return
    // below, so a remote (suppressed) pause still shows as paused.
    if (window.YT.PlayerState && typeof playerState === 'number') {
      const pausedNow = playerState === window.YT.PlayerState.PAUSED;
      advanceStreakRef.current = 0;
      setIsPaused((prev) => (prev === pausedNow ? prev : pausedNow));
    }
    const previous = previousReportedTimeRef.current;
    const delta = Math.abs(current - previous);
    const report = reportStateRef.current;

    if (playerState === window.YT.PlayerState.ENDED) {
      // The end of the video is never the echo of a command, so it is
      // reported even inside the echo window. The stream tells the room it
      // stopped; the end signal itself is local to every page (UI state,
      // auto-advance).
      if (!pageHidden && report && callbacksRef.current.onLocalPause) {
        callbacksRef.current.onLocalPause(current);
      }
      if (callbacksRef.current.onLocalEnd) {
        callbacksRef.current.onLocalEnd(current);
      }
      previousReportedTimeRef.current = current;
      return;
    }

    if (suppressed) {
      if (playerState === window.YT.PlayerState.PLAYING ||
          playerState === window.YT.PlayerState.PAUSED ||
          playerState === window.YT.PlayerState.BUFFERING) {
        // consume one suppression window per state change to avoid loops
        if (now > suppressUntilRef.current - 250) {
          suppressUntilRef.current = 0;
        }
      }
      previousReportedTimeRef.current = current;
      return;
    }

    if (pageHidden) {
      // When the page is hidden, ignore broadcasting local player state changes
      previousReportedTimeRef.current = current;
      return;
    }

    // Only the stream reports what its player did on its own. A controller's
    // or follower's incidental transitions (its own buffering, its autoplay
    // after a load) are nobody else's business; its deliberate actions go
    // out from playLocal/pauseLocal/seekLocal instead.

    if (playerState === window.YT.PlayerState.BUFFERING) {
      if (report && delta > 0.5 && callbacksRef.current.onLocalSeek) {
        callbacksRef.current.onLocalSeek(current);
      }
      previousReportedTimeRef.current = current;
      return;
    }

    if (playerState === window.YT.PlayerState.PAUSED) {
      if (report && delta > 0.5 && callbacksRef.current.onLocalSeek) {
        callbacksRef.current.onLocalSeek(current);
      }
      if (report && callbacksRef.current.onLocalPause) {
        callbacksRef.current.onLocalPause(current);
      }
      previousReportedTimeRef.current = current;
      return;
    }

    if (playerState === window.YT.PlayerState.PLAYING) {
      if (report && delta > 0.5 && callbacksRef.current.onLocalSeek) {
        callbacksRef.current.onLocalSeek(current);
      }
      if (report && callbacksRef.current.onLocalPlay) {
        callbacksRef.current.onLocalPlay(current);
      }
      previousReportedTimeRef.current = current;
      return;
    }

    previousReportedTimeRef.current = current;
  }, []);

  // Track document visibility to avoid broadcasting pause/play when the tab is hidden
  useEffect(() => {
    const onVis = () => {
      try {
        isHiddenRef.current = Boolean(document.hidden);
      } catch (_) {}
    };
    try {
      document.addEventListener('visibilitychange', onVis, { passive: true });
    } catch (_) {}
    onVis();
    return () => {
      try { document.removeEventListener('visibilitychange', onVis); } catch (_) {}
    };
  }, []);

  useEffect(() => {
    if (apiReady) return;

    const previousCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (previousCallback) previousCallback();
      setApiReady(true);
    };

    if (!document.querySelector(`script[src="${YT_API_SRC}"]`)) {
      const tag = document.createElement('script');
      tag.src = YT_API_SRC;
      document.body.appendChild(tag);
    }

    return () => {
      window.onYouTubeIframeAPIReady = previousCallback || null;
    };
  }, [apiReady]);

  useEffect(() => {
    if (!apiReady) return;
    if (!containerRef.current) return;

    if (!playerRef.current) {
      if (!isValidVideoId(videoId)) {
        return; // wait until we have an actual video before creating the player
      }

      const startAt = startSeconds || 0;
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId,
        playerVars: {
          autoplay: autoPlayOnReady ? 1 : 0,
          controls: 0,
          rel: 0,
          modestbranding: 1,
          disablekb: 1,
          fs: 1,
          playsinline: 1,
          start: startAt
        },
        events: {
          onReady: handlePlayerReady,
          onStateChange: handlePlayerStateChange
        }
      });
      if (startAt > 0) {
        setCurrentTime(startAt);
        lastKnownTimeRef.current = startAt;
        previousReportedTimeRef.current = startAt;
      }
      ensurePlayerSize();
      return;
    }

    if (!isValidVideoId(videoId)) {
      playerRef.current.stopVideo();
      setCurrentTime(0);
      lastKnownTimeRef.current = 0;
      previousReportedTimeRef.current = 0;
      return;
    }

    const currentVideo = playerRef.current.getVideoData()?.video_id;
    if (currentVideo !== videoId) {
      const loadStart = startSeconds || 0;
      playerRef.current.loadVideoById({ videoId, startSeconds: loadStart });
      pollTimeRef.current = null;
      advanceStreakRef.current = 0;
      setIsPaused(false);
      setCurrentTime(loadStart);
      lastKnownTimeRef.current = loadStart;
      previousReportedTimeRef.current = loadStart;
    } else {
      const player = playerRef.current;
      if (autoPlayOnReady && player && typeof player.playVideo === 'function') {
        player.playVideo();
      }
    }
    ensurePlayerSize();
  }, [apiReady, videoId, startSeconds, handlePlayerReady, handlePlayerStateChange, ensurePlayerSize, autoPlayOnReady]);

  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, []);

  // Play here. A position is applied (and, for a local action, sent along)
  // only when one is given; a bare local play goes out as a bare command so
  // the room resumes in place.
  const playLocal = useCallback((time, options = {}) => {
    const player = playerRef.current;
    if (!player || typeof player.playVideo !== 'function') return;
    const { source = 'local' } = options;
    markSuppressed();

    const explicitTime = isFiniteTime(time) ? time : undefined;
    if (explicitTime !== undefined) {
      const current = player.getCurrentTime?.() ?? 0;
      if (Math.abs(current - explicitTime) > 0.5) {
        player.seekTo(explicitTime, true);
      }
      setCurrentTime(explicitTime);
      lastKnownTimeRef.current = explicitTime;
    }
    player.playVideo();

    if (source === 'local' && callbacksRef.current.onLocalPlay) {
      callbacksRef.current.onLocalPlay(explicitTime);
    }
  }, [markSuppressed]);

  const pauseLocal = useCallback((time, options = {}) => {
    const player = playerRef.current;
    if (!player || typeof player.pauseVideo !== 'function') return;
    const { source = 'local' } = options;
    markSuppressed();

    const explicitTime = isFiniteTime(time) ? time : undefined;
    const seekToTime = () => {
      if (explicitTime === undefined) return;
      player.seekTo(explicitTime, true);
      setCurrentTime(explicitTime);
      lastKnownTimeRef.current = explicitTime;
    };

    // While playing, pause first and seek second. Seeking a playing player
    // starts a buffering cycle, and pausing mid-buffer can leave YouTube's
    // paused screen (the big play button) out of step with the video. A
    // seek on an already paused player stays paused. In every other state
    // (cued, ended) a seek starts playback, so there the seek must come
    // first and the pause second.
    const state = player.getPlayerState?.();
    const playing = Boolean(window.YT?.PlayerState) &&
      (state === window.YT.PlayerState.PLAYING || state === window.YT.PlayerState.BUFFERING);
    if (playing) {
      player.pauseVideo();
      seekToTime();
    } else {
      seekToTime();
      player.pauseVideo();
    }

    if (source === 'local' && callbacksRef.current.onLocalPause) {
      callbacksRef.current.onLocalPause(explicitTime);
    }
  }, [markSuppressed]);

  const seekLocal = useCallback((time, options = {}) => {
    const player = playerRef.current;
    if (!player || typeof player.seekTo !== 'function' || !isFiniteTime(time)) return;
    const { source = 'local' } = options;
    markSuppressed();
    player.seekTo(time, true);
    setCurrentTime(time);
    lastKnownTimeRef.current = time;

    // A seek always carries its position; that is the whole point of it.
    if (source === 'local' && callbacksRef.current.onLocalSeek) {
      callbacksRef.current.onLocalSeek(time);
    }
  }, [markSuppressed]);

  const getCurrentTime = useCallback(() => {
    const player = playerRef.current;
    if (!player || typeof player.getCurrentTime !== 'function') return undefined;
    return player.getCurrentTime?.();
  }, []);

  // Fall in with the room: ask the server where playback is and apply the
  // answer without broadcasting anything. Returns false when it cannot ask
  // (no socket, not connected, or no player yet).
  const requestServerState = useCallback(() => {
    const { addChannelListener: listen, removeChannelListener: unlisten, emitToChannel: emit, channelConnected: connected } = socketRef.current;
    if (!connected || typeof emit !== 'function' || typeof listen !== 'function' || typeof unlisten !== 'function') {
      return false;
    }
    if (!playerRef.current) {
      return false;
    }

    // Only one sync in flight; a newer request replaces an older one.
    if (pendingStateRequestRef.current) {
      pendingStateRequestRef.current();
      pendingStateRequestRef.current = null;
    }

    let settled = false;
    let timer = null;
    const cancel = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unlisten('player:state_response', handler);
      if (pendingStateRequestRef.current === cancel) {
        pendingStateRequestRef.current = null;
      }
    };
    function handler(state = {}) {
      if (settled) return;
      cancel();

      // The reply must be about the video we are showing NOW, not the one we
      // were showing when we asked (a late reply after a video change).
      const currentVideoId = videoIdRef.current;
      if (state && state.videoId && currentVideoId && state.videoId !== currentVideoId) {
        return;
      }
      if (!state || !isFiniteTime(state.time)) {
        return;
      }
      // A server that has never seen a video (fresh restart) reports no
      // video id and position zero; keep the link's own start offset rather
      // than jumping to zero. A real pause at zero comes with the video id
      // and is honoured.
      let target = state.time;
      const serverIdle = !state.videoId;
      if (serverIdle && !state.playing && target === 0 && startSecondsRef.current > 0) {
        target = startSecondsRef.current;
      }
      if (state.playing) {
        playLocal(target, { source: 'remote' });
      } else {
        pauseLocal(target, { source: 'remote' });
      }
    };

    if (!listen('player:state_response', handler)) {
      return false;
    }
    pendingStateRequestRef.current = cancel;
    timer = setTimeout(cancel, STATE_RESPONSE_TIMEOUT_MS);
    emit('player:state_request');
    return true;
  }, [playLocal, pauseLocal]);

  const setVolumeLocal = useCallback((value) => {
    const player = playerRef.current;
    if (!player) return;

    const clamped = clampVolume(value) ?? 0;
    player.setVolume(clamped);
    setVolume(clamped);

    if (clamped <= 0) {
      if (!player.isMuted?.()) {
        player.mute();
      }
      setMuted(true);
    } else {
      if (player.isMuted?.()) {
        player.unMute();
      }
      setMuted(false);
      lastNonZeroVolumeRef.current = clamped;
    }
  }, []);

  const muteLocal = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    if (typeof volume === 'number' && volume > 0) {
      lastNonZeroVolumeRef.current = volume;
    }
    if (!player.isMuted?.()) {
      player.mute();
    }
    setMuted(true);
  }, [volume]);

  const unmuteLocal = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;

    const fallback = clampVolume(volume) ?? 50;
    const target = lastNonZeroVolumeRef.current && lastNonZeroVolumeRef.current > 0
      ? lastNonZeroVolumeRef.current
      : fallback > 0
        ? fallback
        : 50;

    if (player.isMuted?.()) {
      player.unMute();
    }
    player.setVolume(target);
    setMuted(false);
    setVolume(target);
    lastNonZeroVolumeRef.current = target;
  }, [volume]);

  const toggleMute = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    const mutedFlag = player.isMuted?.();
    if (mutedFlag || muted) {
      unmuteLocal();
    } else {
      muteLocal();
    }
  }, [muteLocal, unmuteLocal, muted]);

  useEffect(() => {
    if (!apiReady) return undefined;

    const interval = setInterval(() => {
      const player = playerRef.current;
      if (!player) {
        return;
      }

      ensurePlayerSize();

      const time = player.getCurrentTime?.();
      const dur = player.getDuration?.();

      if (typeof time === 'number' && !Number.isNaN(time)) {
        setCurrentTime(time);
        lastKnownTimeRef.current = time;

        // A clip ends where its end time says, the same way a natural end
        // does: pause and raise the end signal. Seeking back into the clip
        // re-arms it.
        const clipEnd = endSecondsRef.current;
        if (clipEnd > 0) {
          if (clipEndFiredRef.current && time < clipEnd - 1) {
            clipEndFiredRef.current = false;
          } else if (!clipEndFiredRef.current && time >= clipEnd) {
            const state = player.getPlayerState?.();
            const playing = window.YT?.PlayerState
              ? (state === window.YT.PlayerState.PLAYING || state === window.YT.PlayerState.BUFFERING)
              : true;
            if (playing) {
              clipEndFiredRef.current = true;
              markSuppressed();
              player.pauseVideo?.();
              // The stream reports the stop itself. The paused-state event
              // this pause raises can land inside an echo window (a command
              // arrived moments ago) and be ignored, so do not rely on it.
              if (reportStateRef.current && callbacksRef.current.onLocalPause) {
                callbacksRef.current.onLocalPause(time);
              }
              if (callbacksRef.current.onLocalEnd) {
                callbacksRef.current.onLocalEnd(time);
              }
            }
          }
        }
      }

      // YouTube's paused state, corrected by the clock: if the player says
      // PAUSED but the time keeps moving across polls, the report is stale
      // and the video is really playing.
      const pollState = player.getPlayerState?.();
      if (typeof time === 'number' && !Number.isNaN(time)) {
        const previousPoll = pollTimeRef.current;
        pollTimeRef.current = time;
        advanceStreakRef.current = typeof previousPoll === 'number' && time - previousPoll > 0.05
          ? advanceStreakRef.current + 1
          : 0;
      }
      if (window.YT?.PlayerState && typeof pollState === 'number') {
        const pausedNow = pollState === window.YT.PlayerState.PAUSED && advanceStreakRef.current < 2;
        setIsPaused((prev) => (prev === pausedNow ? prev : pausedNow));
      }

      if (typeof dur === 'number' && dur > 0 && !Number.isNaN(dur)) {
        setDuration(dur);
      }

      const vol = clampVolume(player.getVolume?.());
      if (typeof vol === 'number') {
        setVolume((prev) => {
          if (typeof prev === 'number' && Math.abs(prev - vol) < 0.51) {
            return prev;
          }
          if (vol > 0) {
            lastNonZeroVolumeRef.current = vol;
          }
          return vol;
        });
      }

      const mutedFlag = player.isMuted?.();
      if (typeof mutedFlag === 'boolean') {
        setMuted((prev) => (prev === mutedFlag ? prev : mutedFlag));
      }
    }, 500);

    return () => clearInterval(interval);
  }, [apiReady, videoId, ensurePlayerSize, markSuppressed]);

  // Commands from the room. A missing time means act in place.
  useEffect(() => {
    if (!channelConnected) return;
    const playHandler = ({ time } = {}) => playLocal(time, { source: 'remote' });
    const pauseHandler = ({ time } = {}) => pauseLocal(time, { source: 'remote' });
    const seekHandler = ({ time } = {}) => seekLocal(time, { source: 'remote' });
    const playRegistered = addChannelListener('player:play', playHandler);
    const pauseRegistered = addChannelListener('player:pause', pauseHandler);
    const seekRegistered = addChannelListener('player:seek', seekHandler);

    return () => {
      if (playRegistered) removeChannelListener('player:play', playHandler);
      if (pauseRegistered) removeChannelListener('player:pause', pauseHandler);
      if (seekRegistered) removeChannelListener('player:seek', seekHandler);
    };
  }, [channelConnected, addChannelListener, removeChannelListener, playLocal, pauseLocal, seekLocal]);

  return {
    containerRef,
    playLocal,
    pauseLocal,
    getCurrentTime,
    seekLocal,
    requestServerState,
    setVolume: setVolumeLocal,
    mute: muteLocal,
    unmute: unmuteLocal,
    toggleMute,
    currentTime,
    duration,
    volume,
    muted,
    isPaused,
    playerReady,
    hasVideo: isValidVideoId(videoId)
  };
};
